import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { 
  MapPin, Calendar, ArrowRight, BedDouble, Bath, Home, Shield, Search, 
  DollarSign, SlidersHorizontal, Heart, Building, Wrench, ShieldCheck, CheckCircle, Star, Zap, Lock, FileSignature 
} from 'lucide-react';
import Header from '../../../components/Header';

export default function HabitatLanding() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [filteredListings, setFilteredListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ État pour gérer les favoris
  const [favorites, setFavorites] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const [filters, setFilters] = useState({
    location: '',
    bedrooms: '',
    maxPrice: ''
  });

  useEffect(() => {
    checkUserAndFetchData();
  }, []);

  const checkUserAndFetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);

    // 1. Récupérer les annonces publiées
    const { data: listingsData } = await supabase
      .from('habitat_listings')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false });
    
    if (listingsData) {
      setListings(listingsData);
      setFilteredListings(listingsData);
    }

    // 2. Si l'utilisateur est connecté, récupérer ses favoris
    if (user) {
      const { data: favs } = await supabase
        .from('habitat_favorites')
        .select('listing_id')
        .eq('tenant_id', user.id);
      
      if (favs) {
        setFavorites(favs.map(f => f.listing_id));
      }
    }
    setLoading(false);
  };

  const normalizeString = (str: string) => {
    if (!str) return '';
    return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  useEffect(() => {
    let result = listings;
    if (filters.location) {
      const searchTerm = normalizeString(filters.location);
      result = result.filter(l => normalizeString(l.city).includes(searchTerm) || normalizeString(l.address).includes(searchTerm) || normalizeString(l.title).includes(searchTerm));
    }
    if (filters.bedrooms) result = result.filter(l => l.bedrooms >= parseInt(filters.bedrooms));
    if (filters.maxPrice) result = result.filter(l => l.monthly_rent <= parseInt(filters.maxPrice));
    setFilteredListings(result);
  }, [filters, listings]);

  const executeSearch = () => {
    const element = document.getElementById('listings-results');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') executeSearch();
  };

  // ✅ Fonction pour ajouter/retirer un favori
  const toggleFavorite = async (e: React.MouseEvent, listingId: string) => {
    e.stopPropagation(); // Empêche d'ouvrir la page de l'annonce en cliquant sur le cœur
    
    if (!currentUser) {
      navigate('/login?type=habitat');
      return;
    }

    const isFav = favorites.includes(listingId);
    
    if (isFav) {
      // Retirer des favoris
      setFavorites(favorites.filter(id => id !== listingId));
      await supabase.from('habitat_favorites').delete().match({ tenant_id: currentUser.id, listing_id: listingId });
    } else {
      // Ajouter aux favoris
      setFavorites([...favorites, listingId]);
      await supabase.from('habitat_favorites').insert([{ tenant_id: currentUser.id, listing_id: listingId }]);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans selection:bg-teal-500 selection:text-white pb-20">
      <Header />

      {/* ✅ HERO SECTION (RÉNOVÉE POUR INCLURE LES 3 PROFILS) */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden px-4 bg-slate-900 text-white">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-teal-500/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-sm mb-8 text-sm font-bold text-teal-300 animate-in fade-in slide-in-from-bottom-4">
            <ShieldCheck size={16} /> Conforme à la Loi 25 et aux normes du TAL
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-6" style={{ animationDelay: '100ms' }}>
            La gestion immobilière, <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-400">réinventée pour le Québec.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto font-medium animate-in fade-in slide-in-from-bottom-8" style={{ animationDelay: '200ms' }}>
            Propriétaires, locataires et artisans connectés sur une seule plateforme. Baux électroniques, paiements en ligne et suivi de maintenance en temps réel.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-10" style={{ animationDelay: '300ms' }}>
            <button 
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-5 rounded-2xl font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-xl shadow-teal-500/30 flex items-center justify-center gap-2 group"
            >
              <Building size={18} /> Espace Propriétaire <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/>
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-white/10 text-white border border-white/20 backdrop-blur-md px-8 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-white/20 transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              <Home size={18} /> Espace Locataire
            </button>
          </div>
        </div>
      </section>

      {/* ✅ BARRE DE RECHERCHE (Anciennement dans le Hero) */}
      <section className="relative z-20 -mt-10 max-w-5xl mx-auto px-4">
        <div className="w-full bg-white backdrop-blur-2xl border border-slate-200 p-3 rounded-[2rem] shadow-2xl flex flex-col md:flex-row gap-2">
          <div className="flex-1 flex items-center bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100 hover:bg-slate-100 transition-colors group">
            <Search className="text-teal-500 w-5 h-5 shrink-0" />
            <input type="text" placeholder="Ville ou Quartier (ex: Limoilou)" value={filters.location} onChange={(e) => setFilters({...filters, location: e.target.value})} onKeyDown={handleKeyPress} className="w-full bg-transparent border-none outline-none text-slate-900 placeholder-slate-400 font-bold px-3 focus:ring-0" />
          </div>
          <div className="hidden md:block w-px h-12 bg-slate-200 self-center"></div>
          <div className="md:w-48 flex items-center bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100 hover:bg-slate-100 transition-colors">
            <BedDouble className="text-slate-400 w-5 h-5 shrink-0" />
            <select value={filters.bedrooms} onChange={(e) => setFilters({...filters, bedrooms: e.target.value})} className="w-full bg-transparent border-none outline-none text-slate-700 font-bold px-2 focus:ring-0 cursor-pointer appearance-none">
              <option value="" className="text-slate-900">Chambres</option>
              <option value="1" className="text-slate-900">1+ (3 ½)</option>
              <option value="2" className="text-slate-900">2+ (4 ½)</option>
              <option value="3" className="text-slate-900">3+ (5 ½)</option>
            </select>
          </div>
          <div className="hidden md:block w-px h-12 bg-slate-200 self-center"></div>
          <div className="md:w-48 flex items-center bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100 hover:bg-slate-100 transition-colors">
            <DollarSign className="text-slate-400 w-5 h-5 shrink-0" />
            <select value={filters.maxPrice} onChange={(e) => setFilters({...filters, maxPrice: e.target.value})} className="w-full bg-transparent border-none outline-none text-slate-700 font-bold px-2 focus:ring-0 cursor-pointer appearance-none">
              <option value="" className="text-slate-900">Loyer Max</option>
              <option value="1000" className="text-slate-900">Max 1000$</option>
              <option value="1500" className="text-slate-900">Max 1500$</option>
              <option value="2000" className="text-slate-900">Max 2000$</option>
              <option value="3000" className="text-slate-900">Max 3000$</option>
            </select>
          </div>
          <button onClick={executeSearch} className="bg-slate-900 text-white md:w-auto w-full px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2">
            Rechercher
          </button>
        </div>
      </section>

      {/* ✅ 3 PILIERS (FEATURES) */}
      <section className="py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4">Un écosystème complet</h2>
            <p className="text-slate-500 font-medium">Tout ce dont vous avez besoin, peu importe votre rôle.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pilier Propriétaire */}
            <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building size={32} />
              </div>
              <h3 className="text-2xl font-black mb-4">Propriétaires</h3>
              <ul className="space-y-4 text-slate-600 font-medium">
                <li className="flex items-start gap-3"><CheckCircle className="text-blue-500 shrink-0 mt-0.5" size={18} /> Baux électroniques conformes au TAL.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-blue-500 shrink-0 mt-0.5" size={18} /> Perception des loyers automatisée.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-blue-500 shrink-0 mt-0.5" size={18} /> Enquêtes de crédit premium.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-blue-500 shrink-0 mt-0.5" size={18} /> Calculateur de hausse de loyer intégré.</li>
              </ul>
            </div>

            {/* Pilier Locataire */}
            <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Home size={32} />
              </div>
              <h3 className="text-2xl font-black mb-4">Locataires</h3>
              <ul className="space-y-4 text-slate-600 font-medium">
                <li className="flex items-start gap-3"><CheckCircle className="text-teal-500 shrink-0 mt-0.5" size={18} /> Dossier de candidature unique et sécurisé.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-teal-500 shrink-0 mt-0.5" size={18} /> Paiement du loyer par carte ou virement.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-teal-500 shrink-0 mt-0.5" size={18} /> Signalement de problèmes techniques.</li>
                <li className="flex items-start gap-3"><CheckCircle className="text-teal-500 shrink-0 mt-0.5" size={18} /> Suivi GPS en direct de l'arrivée de l'artisan.</li>
              </ul>
            </div>

            {/* Pilier Artisan */}
            <div className="bg-slate-900 text-white p-10 rounded-[2.5rem] shadow-2xl hover:shadow-teal-500/20 hover:-translate-y-2 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 blur-3xl rounded-full pointer-events-none"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Wrench size={32} />
                </div>
                <h3 className="text-2xl font-black mb-4 flex items-center gap-2">Artisans Pros <Zap size={20} className="text-amber-400"/></h3>
                <ul className="space-y-4 text-slate-300 font-medium mb-8">
                  <li className="flex items-start gap-3"><CheckCircle className="text-amber-400 shrink-0 mt-0.5" size={18} /> Accès aux appels d'offres locaux.</li>
                  <li className="flex items-start gap-3"><CheckCircle className="text-amber-400 shrink-0 mt-0.5" size={18} /> Soumission de devis en 1 clic.</li>
                  <li className="flex items-start gap-3"><CheckCircle className="text-amber-400 shrink-0 mt-0.5" size={18} /> Partage de position GPS au client.</li>
                  <li className="flex items-start gap-3"><CheckCircle className="text-amber-400 shrink-0 mt-0.5" size={18} /> Génération de rapports comptables.</li>
                </ul>
                <button onClick={() => navigate('/login?type=forfeopro')} className="w-full bg-amber-500 text-slate-900 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-amber-400 transition-colors shadow-lg">
                  Devenir partenaire Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RÉSULTATS DES LOGEMENTS */}
      <section id="listings-results" className="py-24 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-24 border-t border-slate-200">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
              {filters.location ? `Logements à ${filters.location}` : 'Logements disponibles'}
            </h2>
            <p className="text-slate-500 mt-2 font-medium text-lg">
              {filteredListings.length} résultat{filteredListings.length > 1 ? 's' : ''} trouvé{filteredListings.length > 1 ? 's' : ''}.
            </p>
          </div>
          
          <button onClick={() => navigate('/login?type=habitat')} className="hidden md:flex items-center gap-3 bg-white border border-slate-200 px-5 py-3 rounded-2xl hover:border-teal-400 hover:shadow-lg transition-all group">
            <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center group-hover:bg-teal-500 transition-colors">
              <Shield className="text-teal-600 w-5 h-5 group-hover:text-white transition-colors" />
            </div>
            <div className="text-left">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400 group-hover:text-teal-600 transition-colors">Dossier Unique</p>
              <p className="text-sm font-bold text-slate-900">Créer mon profil sécurisé</p>
            </div>
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-500"></div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Chargement des annonces...</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-[3rem] p-16 md:p-24 text-center max-w-3xl mx-auto shadow-sm">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6"><Search className="w-10 h-10 text-slate-400" /></div>
            <h3 className="text-2xl font-black text-slate-800 mb-3">Aucun logement ne correspond</h3>
            <button onClick={() => { setFilters({ location: '', bedrooms: '', maxPrice: '' }); executeSearch(); }} className="bg-slate-900 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-slate-800 transition shadow-lg">
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredListings.map((listing, index) => {
              const isFav = favorites.includes(listing.id);
              
              return (
                <div key={listing.id} onClick={() => navigate(`/habitat/logement/${listing.id}`)} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] hover:border-teal-300 transition-all duration-500 cursor-pointer group flex flex-col relative">
                  
                  {/* ✅ BOUTON CŒUR (FAVORI) */}
                  <button 
                    onClick={(e) => toggleFavorite(e, listing.id)}
                    className="absolute top-4 right-4 z-20 p-3 bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:scale-110 transition-transform"
                  >
                    <Heart className={`w-5 h-5 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-400 hover:text-red-500'}`} />
                  </button>

                  <div className="h-64 relative overflow-hidden bg-slate-100">
                    <img src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1de2d9d000'} alt={listing.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md px-5 py-2.5 rounded-full text-lg font-black text-slate-900 shadow-xl border border-white/50 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      {listing.monthly_rent} <span className="text-xs text-slate-500">$/m</span>
                    </div>
                  </div>
                  
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 text-xs font-black text-teal-600 uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-lg w-fit mb-3">
                      <MapPin size={12} /> {listing.city}
                    </div>
                    
                    <h3 className="text-2xl font-black text-slate-900 leading-tight mb-4 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {listing.title}
                    </h3>
                    
                    <div className="flex items-center gap-3 text-sm font-bold text-slate-500 mb-8 mt-auto">
                      <span className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-100 flex-1 justify-center"><BedDouble size={16} className="text-slate-400"/> {listing.bedrooms} ch.</span>
                      <span className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-100 flex-1 justify-center"><Bath size={16} className="text-slate-400"/> {listing.bathrooms} sdb</span>
                    </div>

                    <div className="pt-5 border-t border-slate-100 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-slate-500 font-bold">
                        <Calendar size={16} className="text-slate-400" />
                        {listing.available_from ? new Date(listing.available_from).toLocaleDateString('fr-CA') : 'Maintenant'}
                      </span>
                      <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-teal-500 group-hover:text-white text-slate-400 transition-colors">
                        <ArrowRight size={18} className="-rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ✅ TARIFICATION (NOUVEAU) */}
      <section className="py-24 px-4 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4">Des tarifs simples et transparents</h2>
            <p className="text-slate-500 font-medium">Commencez gratuitement, passez au niveau supérieur quand vous êtes prêt.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Plan Gratuit */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm flex flex-col">
              <div className="mb-6">
                <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest">Plan Découverte</span>
                <h3 className="text-4xl font-black mt-4 mb-2">0 $ <span className="text-lg text-slate-400 font-medium">/ mois</span></h3>
                <p className="text-slate-500 text-sm">Parfait pour le propriétaire d'un seul logement.</p>
              </div>
              <ul className="space-y-4 mb-10 flex-1">
                <li className="flex items-center gap-3 text-slate-700 font-bold"><CheckCircle size={18} className="text-teal-500"/> 1 immeuble maximum</li>
                <li className="flex items-center gap-3 text-slate-700 font-bold"><CheckCircle size={18} className="text-teal-500"/> Gestion des candidatures</li>
                <li className="flex items-center gap-3 text-slate-700 font-bold"><CheckCircle size={18} className="text-teal-500"/> Centre de maintenance basique</li>
                <li className="flex items-center gap-3 text-slate-400 font-medium opacity-50"><Lock size={18}/> Baux électroniques illimités</li>
                <li className="flex items-center gap-3 text-slate-400 font-medium opacity-50"><Lock size={18}/> Perception des loyers en ligne</li>
              </ul>
              <button onClick={() => navigate('/login')} className="w-full bg-slate-100 text-slate-900 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-slate-200 transition-colors">
                Commencer gratuitement
              </button>
            </div>

            {/* Plan Pro */}
            <div className="bg-slate-900 text-white p-10 rounded-[3rem] border border-slate-800 shadow-2xl flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 blur-[80px] pointer-events-none"></div>
              <div className="relative z-10 mb-6">
                <span className="bg-teal-500 text-slate-900 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest shadow-sm">Plan Pro 🌟</span>
                <h3 className="text-4xl font-black mt-4 mb-2">29,99 $ <span className="text-lg text-teal-100 font-medium">/ mois</span></h3>
                <p className="text-slate-400 text-sm">Le centre de commande ultime pour votre parc immobilier.</p>
              </div>
              <ul className="space-y-4 mb-10 flex-1 relative z-10">
                <li className="flex items-center gap-3 font-bold"><CheckCircle size={18} className="text-teal-400"/> Immeubles et annonces illimités</li>
                <li className="flex items-center gap-3 font-bold"><CheckCircle size={18} className="text-teal-400"/> Baux électroniques illimités</li>
                <li className="flex items-center gap-3 font-bold"><CheckCircle size={18} className="text-teal-400"/> Perception des loyers automatisée</li>
                <li className="flex items-center gap-3 font-bold"><CheckCircle size={18} className="text-teal-400"/> Messagerie et suivi GPS des artisans</li>
                <li className="flex items-center gap-3 font-bold"><CheckCircle size={18} className="text-teal-400"/> Accès au service conciergerie d'enquête</li>
              </ul>
              <button onClick={() => navigate('/login')} className="w-full bg-teal-500 text-slate-900 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-teal-400 transition-colors shadow-lg shadow-teal-500/20 relative z-10">
                Débloquer Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ CTA FINAL */}
      <section className="py-24 px-4 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-teal-500 to-blue-600 rounded-[3rem] p-12 text-center text-white shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-black mb-6">Prêt à moderniser votre gestion ?</h2>
            <p className="text-lg md:text-xl text-teal-50 font-medium mb-10 max-w-2xl mx-auto">
              Rejoignez des centaines de propriétaires et locataires au Québec qui utilisent Forfeo pour simplifier leur quotidien.
            </p>
            <button onClick={() => navigate('/login')} className="bg-slate-900 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl hover:scale-105 flex items-center justify-center gap-2 mx-auto">
              Créer mon compte <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER EXISTANT */}
      <footer className="bg-[#0B1120] text-slate-400 py-12 text-center border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-bold">© {new Date().getFullYear()} Forfeo Explore Québec. Tous droits réservés.</p>
          <div className="flex gap-6 text-sm font-medium">
            <a href="#" className="hover:text-white transition">Confidentialité</a>
            <a href="#" className="hover:text-white transition">Conditions</a>
            <a href="#" className="hover:text-white transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
